import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, Plane, Route as RouteIcon, Newspaper, MessageSquareWarning, PlaneTakeoff, Map as MapIcon, Info, Camera, TrendingUp, MessageCircle, BookOpen, Stamp, Store, Armchair } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { AviationBanner, Spinner, Pill, StatBox, fmtNum, TabBar } from "../components/common";
import TopBoard from "../components/TopBoard";
import AiContentPack from "../components/AiContentPack";
import MapView from "../components/MapView";
import NewsFeed from "../components/NewsFeed";
import WikiPanel from "../components/WikiPanel";
import { SocialPulseGuide } from "../components/SocialPulseGuide";
import { AirportNearby } from "../components/AirportNearby";
import { DirectionsButton } from "../components/DirectionsButton";
import { IndiaAirportGuide } from "../components/IndiaAirportGuide";
import AiAirportProfile from "../components/AiAirportProfile";
import AirportContacts from "../components/AirportContacts";
import PhotoGallery from "../components/PhotoGallery";
import DiscussionBoard from "../components/DiscussionBoard";
import AirportHotels from "../components/AirportHotels";
import FeaturedArticle from "../components/FeaturedArticle";
import AiEntityProfile from "../components/AiEntityProfile";
import AirportShops from "../components/AirportShops";
import AirportMaps from "../components/AirportMaps";
import AirportLounges from "../components/AirportLounges";
import AirportExplore from "../components/AirportExplore";
import { pickAirportBg } from "../lib/assets";

const TABS = [
  { key: "overview", label: "Overview", icon: <Info className="w-4 h-4" /> },
  { key: "maps", label: "Maps & Directions", icon: <MapIcon className="w-4 h-4" /> },
  { key: "lounges", label: "Lounges", icon: <Armchair className="w-4 h-4" /> },
  { key: "shops", label: "Dining & Shopping", icon: <Store className="w-4 h-4" /> },
  { key: "photos", label: "Photos", icon: <Camera className="w-4 h-4" /> },
  { key: "articles", label: "Articles", icon: <Newspaper className="w-4 h-4" /> },
  { key: "news", label: "News", icon: <Newspaper className="w-4 h-4" /> },
  { key: "trending", label: "Trending", icon: <TrendingUp className="w-4 h-4" /> },
  { key: "complaints", label: "Discussion & Complaints", icon: <MessageSquareWarning className="w-4 h-4" /> },
  { key: "stories", label: "Stories", icon: <BookOpen className="w-4 h-4" /> },
];

export default function AirportDetail() {
  const { code } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [searchParams] = useSearchParams();
  const initialTab = (p) => (["shops", "maps", "lounges"].includes(p.get("tab")) ? p.get("tab") : "overview");
  const [tab, setTab] = useState(initialTab(searchParams));

  useEffect(() => {
    setA(null); setErr(false);
    setTab(initialTab(searchParams));
    api.get(`/airports/${code}`).then((r) => {
      setA(r.data);
      api.get("/complaints", { params: { entity_type: "airport", entity_id: r.data.id, limit: 5 } })
        .then((c) => setComplaints(c.data.items)).catch(() => {});
    }).catch(() => setErr(true));
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Airport not found.</div>;
  if (!a) return <Spinner full />;

  const HDR = pickAirportBg(a.id || a.name);
  const newsPath = `/airports/${a.iata_code || a.id}/news?limit=18`;

  return (
    <div data-testid="airport-detail">
      <TopBoard entity={a} kind="airport"
        badge={a.iata_code && `${a.iata_code} · ${a.type?.replace(/_/g, " ")}`}
        title={a.name}
        subtitle={a.municipality ? `${a.municipality}, ${a.country_name || ""}` : a.country_name}
      />
      <TabBar tabs={TABS} active={tab} onChange={setTab} attached />
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-blue-900/80" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <Link to="/airports" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Airports</Link>
          <div className="flex items-center gap-2 mb-2">
            {a.iata_code && <span className="font-mono text-2xl text-blue-400 font-500">{a.iata_code}</span>}
            <Pill tone="blue">{a.type?.replace("_", " ")}</Pill>
          </div>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white max-w-3xl">{a.name}</h1>
          <div className="flex items-center gap-1.5 text-zinc-200 mt-3 text-lg sm:text-xl font-500"><MapPin className="w-5 h-5" />
            {a.municipality ? (
              <Link to={`/cities/${encodeURIComponent(a.municipality)}?country=${a.iso_country || ""}${a.iso_region ? `&region=${a.iso_region}` : ""}`} className="text-white hover:text-blue-300 underline decoration-white/40 underline-offset-4 transition-colors" data-testid="airport-city-link">
                {a.municipality}
              </Link>
            ) : "—"}, {a.country_name}
          </div>
          {a.municipality && (
            <Link to={`/cities/${encodeURIComponent(a.municipality)}?country=${a.iso_country || ""}${a.iso_region ? `&region=${a.iso_region}` : ""}`} data-testid="airport-explore-city"
              className="inline-flex items-center gap-2 mt-4 mr-3 bg-blue-600 text-white px-4 py-2.5 font-600 text-sm hover:bg-blue-700 transition-colors">
              <BookOpen className="w-4 h-4" /> Explore {a.municipality} city guide
            </Link>
          )}
          <button onClick={() => { setTab("maps"); window.scrollTo({ top: 0 }); }} data-testid="airport-maps-button"
            className="inline-flex items-center gap-2 mt-4 bg-emerald-600 text-white px-4 py-2.5 font-600 text-sm hover:bg-emerald-700 transition-colors">
            <MapIcon className="w-4 h-4" /> Airport Directions &amp; Maps
          </button>
          <DirectionsButton lat={a.latitude} lon={a.longitude} name={`${a.name}${a.iata_code ? ` (${a.iata_code})` : ""}`}
            label="Navigate to this airport" testid="airport-navigate-btn"
            className="mt-4 ml-3 !bg-white !py-2.5 !px-4 !text-sm" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300">
        <StatBox value={fmtNum(a.routes_count || 0)} label="Routes out" testid="airport-routes-count" />
        <StatBox value={fmtNum(a.destinations_count || 0)} label="Destinations" />
        <StatBox value={a.icao_code || "—"} label="ICAO" />
        <StatBox value={a.elevation_ft ? `${a.elevation_ft} ft` : "—"} label="Elevation" />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-6">
        <AirportExplore iata={a.iata_code} onOpenLounges={() => setTab("lounges")} onOpenShops={() => setTab("shops")} />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {tab === "overview" && (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              <AiEntityProfile type="airport" code={a.iata_code || a.id} name={a.name} />
              <AiContentPack code={a.iata_code || a.id} kind="airport" />
              <IndiaAirportGuide iata={a.iata_code} />
              <FeaturedArticle entityType="airport" entityId={a.iata_code || a.id} />
              {a.map_center && a.route_map?.length > 0 && (
                <div>
                  <h2 className="font-heading font-900 text-violet-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><MapIcon className="w-6 h-6" /> Route map <span className="text-sm font-500 text-muted-foreground">({a.route_map.length} destinations)</span></h2>
                  <MapView
                    center={a.map_center} zoom={4} height="420px"
                    origin={{ center: a.map_center, label: `${a.name} (${a.iata_code})` }}
                    lines={a.route_map.map((d) => ({ from: a.map_center, to: [d.lat, d.lon] }))}
                    markers={a.route_map.map((d) => ({ lat: d.lat, lon: d.lon, radius: 2.5, popup: `${d.name || d.dest}` }))}
                  />
                </div>
              )}

              <div>
                <h2 className="font-heading font-900 text-blue-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><Plane className="w-6 h-6" /> Airlines serving this airport</h2>
                {a.airlines?.length ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {a.airlines.map((al) => (
                      <Link key={al.id} to={`/airlines/${al.id}`} className="border border-zinc-300 bg-white px-3 py-2.5 hover:border-primary transition-colors">
                        <div className="text-sm font-medium truncate">{al.name}</div>
                        <div className="font-mono text-xs text-primary">{al.iata}</div>
                      </Link>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No scheduled airline data available.</p>}
              </div>

              <div>
                <h2 className="font-heading font-900 text-rose-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><RouteIcon className="w-6 h-6" /> Routes</h2>
                {a.routes?.length ? (
                  <div className="border border-zinc-300 bg-white divide-y divide-zinc-200 max-h-96 overflow-auto">
                    {a.routes.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="font-mono"><span className="text-primary">{r.source_airport}</span> → {r.destination_airport}</span>
                        <span className="font-mono text-xs text-muted-foreground">{r.airline_iata} · {r.equipment || ""}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No route data available.</p>}
              </div>

              {a.aircraft_types?.length > 0 && (
                <div>
                  <h2 className="font-heading font-900 text-emerald-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><PlaneTakeoff className="w-6 h-6" /> Aircraft operating here</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {a.aircraft_types.slice(0, 12).map((f) => (
                      <Link key={f.iata} to={`/aircraft/${encodeURIComponent(f.iata)}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-2.5 hover:border-primary transition-colors" data-testid={`airport-aircraft-${f.iata}`}>
                        <span className="text-sm font-medium truncate">{f.name}</span>
                        <span className="font-mono text-xs text-muted-foreground shrink-0">{f.route_count} routes</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <AirportContacts code={a.iata_code || a.id} />
              <AirportNearby iata={a.iata_code || a.id} />
              <AirportHotels airportName={a.name} iata={a.iata_code} />
              {a.municipality && a.country_name && (
                <SocialPulseGuide country={a.country_name} city={a.municipality} />
              )}
              {a.iso_country && ["large_airport", "medium_airport"].includes(a.type) && (
                <div className="border border-[#0e7490]/40 bg-cyan-50/50 p-5" data-testid="airport-visa-card">
                  <h3 className="font-heading font-700 flex items-center gap-2 mb-2"><Stamp className="w-4 h-4 text-[#0e7490]" /> Passport, Visa &amp; Immigration</h3>
                  <p className="text-sm text-zinc-700 mb-3">Flying internationally via {a.iata_code || a.name}? Check entry, transit and customs requirements for {a.country_name} from official government sources.</p>
                  <div className="flex flex-col gap-1.5 text-sm font-700">
                    <Link to={`/visas/${a.iso_country}`} data-testid="airport-visa-country-link" className="text-[#0e7490] hover:underline">{a.country_name} visa &amp; entry information →</Link>
                    <Link to="/visas" className="text-[#0e7490] hover:underline">Embassy &amp; consulate finder →</Link>
                  </div>
                </div>
              )}
              <WikiPanel title={a.name} />
              <div>
                <h3 className="font-heading font-700 flex items-center gap-2 mb-3"><Newspaper className="w-4 h-4 text-primary" /> Latest news</h3>
                <NewsFeed path={`/airports/${a.iata_code || a.id}/news?limit=6`} compact />
              </div>
            </div>
          </div>
        )}

        {tab === "maps" && <AirportMaps iata={a.iata_code} airportName={a.name} />}

        {tab === "lounges" && <AirportLounges iata={a.iata_code} onReport={(l) => {
          const detail = window.prompt(`Suggest an update for "${l.name}" (closed, moved, access changed, wrong hours…):`);
          if (detail && detail.trim().length >= 5) {
            api.post("/transport/reports", { mode: "airport", entity_type: "lounge", entity_id: l.id, issue_type: "incorrect_information", detail: detail.trim() })
              .then(() => toast.success("Thank you — your report entered the verification queue."))
              .catch((e) => toast.error(e.response?.status === 401 ? "Please sign in to report an update." : "Could not submit the report."));
          }
        }} />}

        {tab === "shops" && <AirportShops iata={a.iata_code} airportName={a.name} />}

        {tab === "photos" && <PhotoGallery entityType="airport" entityId={a.id} />}

        {tab === "articles" && (
          <div className="space-y-10">
            {["large_airport", "medium_airport"].includes(a.type)
              ? <AiAirportProfile code={a.iata_code || a.id} seed={a.id || a.name} name={a.name} />
              : <p className="text-sm text-muted-foreground">A detailed profile is available for major airports. Check the News tab for the latest headlines.</p>}
            {a.articles?.length > 0 && (
              <div>
                <h2 className="font-heading font-900 text-blue-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><Newspaper className="w-6 h-6" /> Featured articles</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {a.articles.map((ar) => (
                    <Link key={ar.slug} to={`/articles/${ar.slug}`} className="border border-zinc-300 bg-white p-5 hover:border-primary transition-colors">
                      <Newspaper className="w-5 h-5 text-primary mb-2" />
                      <div className="font-heading font-700 leading-snug">{ar.title}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "news" && <NewsFeed path={newsPath} />}

        {tab === "trending" && (
          <div>
            <div className="flex items-center gap-2 mb-4"><Pill tone="blue">Trending</Pill><span className="text-sm text-muted-foreground">Most talked-about {a.name} headlines right now</span></div>
            <NewsFeed path={newsPath} />
          </div>
        )}

        {tab === "complaints" && (
          <div className="space-y-10">
            <div className="border border-zinc-300 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2"><MessageSquareWarning className="w-5 h-5 text-destructive" /><span className="font-700">Have a formal complaint about {a.name}?</span></div>
              <Link to={`/complaints/new?type=airport&id=${a.id}&name=${encodeURIComponent(a.name)}&iata=${a.iata_code || ""}`} className="px-4 py-2 bg-destructive text-white text-sm font-600 hover:opacity-90" data-testid="airport-file-complaint">File a complaint</Link>
            </div>
            {complaints.length > 0 && (
              <div>
                <h3 className="font-heading font-700 mb-3">Recent complaints</h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {complaints.map((c) => (
                    <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} className="block border border-zinc-300 bg-white p-3 hover:border-primary transition-colors">
                      <div className="text-sm font-medium line-clamp-1">{c.title}</div>
                      <div className="text-xs font-mono text-muted-foreground mt-1">▲ {c.upvote_count} · {c.category}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <DiscussionBoard entityType="airport" entityId={a.id} entityName={a.name} kind="comment" title={`${a.name} discussion board`} />
          </div>
        )}

        {tab === "stories" && <DiscussionBoard entityType="airport" entityId={a.id} entityName={a.name} kind="story" title={`Passenger stories about ${a.name}`} />}
      </div>
    </div>
  );
}
